import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { IServiceUserInfo } from "../../datasolvr/ServicesFacade";
import { planEatApi } from "../../planEatApi/PlanEatApi";
import { ICompanyContent, IEaterGroupContent, IOfficeContent, IUserContent, IVersionedObject } from "../../planEatApi/PlanEatFacade";
import { errorMessage } from "./sysadminUtils";

export interface SysadminData {
    companies: IVersionedObject<ICompanyContent>[];
    offices: IVersionedObject<IOfficeContent>[];
    eaterGroups: IVersionedObject<IEaterGroupContent>[];
    users: IVersionedObject<IUserContent>[];
    serviceUsers: IServiceUserInfo[];
    loading: boolean;
    error?: string;
    reload: () => Promise<void>;
    companyById: (id?: string) => ICompanyContent | undefined;
    officeById: (id?: string) => IOfficeContent | undefined;
    eaterGroupById: (id?: string) => IEaterGroupContent | undefined;
    /** Company chosen in the selector; undefined means "All companies". */
    selectedCompanyId?: string;
    setSelectedCompanyId: (companyId?: string) => void;
    /** Company an office or eater group belongs to. */
    companyIdOfOffice: (officeId?: string) => string | undefined;
    companyIdOfEaterGroup: (eaterGroupId?: string) => string | undefined;
    /** True when nothing is selected or the given company is the selected one. */
    matchesSelectedCompany: (companyId?: string) => boolean;
}

const SELECTED_COMPANY_KEY = "sysadmin.companyId";
const ALL_COMPANIES = "__all__";
const DEFAULT_COMPANY_NAME = "republikken aps";

function readStoredCompany(): string | null {
    try {
        return localStorage.getItem(SELECTED_COMPANY_KEY);
    } catch {
        return null;
    }
}

const SysadminDataContext = createContext<SysadminData | undefined>(undefined);

function byId<T extends { id: string }>(items: IVersionedObject<T>[]): Map<string, T> {
    return new Map(items.map(item => [item.content.id, item.content]));
}

/** Loads all sysadmin collections once (in parallel) and shares them between the sysadmin pages. */
export const SysadminDataProvider: React.FC = ({ children }) => {
    const [companies, setCompanies] = useState<IVersionedObject<ICompanyContent>[]>([]);
    const [offices, setOffices] = useState<IVersionedObject<IOfficeContent>[]>([]);
    const [eaterGroups, setEaterGroups] = useState<IVersionedObject<IEaterGroupContent>[]>([]);
    const [users, setUsers] = useState<IVersionedObject<IUserContent>[]>([]);
    const [serviceUsers, setServiceUsers] = useState<IServiceUserInfo[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>();
    // null = not chosen yet (resolved from localStorage / the default company once companies are loaded)
    const [storedCompany, setStoredCompany] = useState<string | null>(readStoredCompany);

    const setSelectedCompanyId = useCallback((companyId?: string) => {
        const value = companyId ?? ALL_COMPANIES;
        try {
            localStorage.setItem(SELECTED_COMPANY_KEY, value);
        } catch {
            // Selection is then only kept for this session
        }
        setStoredCompany(value);
    }, []);

    const reload = useCallback(async () => {
        setLoading(true);
        const [companiesRes, officesRes, eaterGroupsRes, usersRes, serviceUsersRes] = await Promise.allSettled([
            planEatApi.getCompanies(),
            planEatApi.getOffices(),
            planEatApi.getEaterGroups(),
            planEatApi.getUsers(),
            planEatApi.getServiceUsers(),
        ]);
        const errors: string[] = [];
        if (companiesRes.status === "fulfilled") setCompanies(companiesRes.value.data); else errors.push("companies: " + errorMessage(companiesRes.reason));
        if (officesRes.status === "fulfilled") setOffices(officesRes.value.data); else errors.push("offices: " + errorMessage(officesRes.reason));
        if (eaterGroupsRes.status === "fulfilled") setEaterGroups(eaterGroupsRes.value.data); else errors.push("eater groups: " + errorMessage(eaterGroupsRes.reason));
        if (usersRes.status === "fulfilled") setUsers(usersRes.value.data); else errors.push("users: " + errorMessage(usersRes.reason));
        if (serviceUsersRes.status === "fulfilled") setServiceUsers(serviceUsersRes.value.data); else errors.push("application users: " + errorMessage(serviceUsersRes.reason));
        setError(errors.length > 0 ? "Could not load " + errors.join(", ") : undefined);
        setLoading(false);
    }, []);

    useEffect(() => {
        reload();
    }, [reload]);

    const value = useMemo<SysadminData>(() => {
        const companyMap = byId(companies);
        const officeMap = byId(offices);
        const eaterGroupMap = byId(eaterGroups);

        let selectedCompanyId: string | undefined;
        if (storedCompany && storedCompany !== ALL_COMPANIES && (loading || companyMap.has(storedCompany))) {
            selectedCompanyId = storedCompany;
        } else if (storedCompany !== ALL_COMPANIES) {
            selectedCompanyId = companies.find(c => (c.content.companyName ?? "").trim().toLowerCase() === DEFAULT_COMPANY_NAME)?.content.id;
        }

        const companyIdOfOffice = (officeId?: string) => (officeId ? officeMap.get(officeId)?.companyId : undefined);
        const companyIdOfEaterGroup = (eaterGroupId?: string) => companyIdOfOffice(eaterGroupId ? eaterGroupMap.get(eaterGroupId)?.officeId : undefined);
        return {
            companies, offices, eaterGroups, users, serviceUsers, loading, error, reload,
            companyById: id => (id ? companyMap.get(id) : undefined),
            officeById: id => (id ? officeMap.get(id) : undefined),
            eaterGroupById: id => (id ? eaterGroupMap.get(id) : undefined),
            selectedCompanyId,
            setSelectedCompanyId,
            companyIdOfOffice,
            companyIdOfEaterGroup,
            matchesSelectedCompany: companyId => !selectedCompanyId || companyId === selectedCompanyId,
        };
    }, [companies, offices, eaterGroups, users, serviceUsers, loading, error, reload, storedCompany, setSelectedCompanyId]);

    return <SysadminDataContext.Provider value={value}>{children}</SysadminDataContext.Provider>;
};

export function useSysadminData(): SysadminData {
    const context = useContext(SysadminDataContext);
    if (!context) {
        throw new Error("useSysadminData must be used inside SysadminDataProvider");
    }
    return context;
}
